import Core                   from '@/core/Core';
import CharacteristicUnit     from '@/domains/CharacteristicUnits/CharacteristicUnit';
import CharacteristicUnitsDAO from '@/domains/CharacteristicUnits/CharacteristicUnitsDAO';
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';

class CharacteristicUnitsService {

  static async get(id: number): Promise<CharacteristicUnit | null> {
    const result = await this.getList({ id });
    return result[0] ?? null;
  }

  static async getListPaginated(
    params?: Parameters<typeof CharacteristicUnitsDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    }
  ): Promise<{ data: CharacteristicUnit[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await CharacteristicUnitsDAO.getList(filters, pagination, sort);

    return {
      data: Core.utils.hydrateArray(response.data, CharacteristicUnit.fromServer),
      pagination: response.meta.pagination,
    };
  }

  static async getList(
    params?: Parameters<typeof CharacteristicUnitsDAO.getList>[0]
  ): Promise<CharacteristicUnit[]> {
    const result = await this.getListPaginated(params);
    return result.data;
  }

  static async add(characteristicUnit: CharacteristicUnit): Promise<boolean> {
    const response = await CharacteristicUnitsDAO.add(characteristicUnit);

    if (response.ok) {
      Core.showNotification.success('Единица измерения добавлена');
    }

    return response.ok;
  }

  static async update(characteristicUnit: CharacteristicUnit): Promise<boolean> {
    const response = await CharacteristicUnitsDAO.update(characteristicUnit);

    if (response.ok) {
      Core.showNotification.success('Единица измерения обновлена');
    }

    return response.ok;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await CharacteristicUnitsDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Единица измерения удалена');
    }

    return response.ok;
  }

  /**
   * Удаление с подтверждением. Используется из контекстного меню.
   * Старый delete(id) оставлен для массовых операций без UX.
   */
  static async deleteWithConfirmation(characteristicUnit: CharacteristicUnit): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить эту единицу измерения?',
      characteristicUnit.name,
    );

    if (confirmed === true) {
      return await this.delete(characteristicUnit.id);
    }
    else {
      return false;
    }
  }

  /**
   * Открыть карточку единицы измерения характеристики.
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static showCard(characteristicUnit: CharacteristicUnit | null): Promise<boolean> {
    return Core.dialogs.CharacteristicUnitCard.showAsPromise({ entity: characteristicUnit });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (unit: CharacteristicUnit) => { return this.showCard(unit); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'История действий',
        'history',
        (unit: CharacteristicUnit) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'characteristicUnit', entityId: unit.id, showAddComment: false }); },
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (unit: CharacteristicUnit) => { return this.deleteWithConfirmation(unit); },
      ),
    ];
  }
}

export default CharacteristicUnitsService;